import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MultiSelect } from 'primereact/multiselect';

// Api
import TagApi from '../../../api/TagApi';
import api from '../../../api/Base';

// Hooks
import { useAlertRuleForm } from '../../../hooks/useAlertRuleForm';

// Types
import AlertRules from "../../../types/Alerts/AlertRules";
import InstanceTarget from "../../../types/instance/InstanceTarget";
import HostTarget from "../../../types/host/HostTarget";
import { EVALUATION_WINDOW_MAX, FREQUENCY_MAX } from "../../../types/Alerts/AlertRuleFormState";

// Constants
import { DATA_LOADED, generalErrorMessage } from '../../../component/Constants';

// Helpers
import ConditionalRender from "../../../helpers/ConditionalRender";

// Components
import Alert from "../../../component/Alert";
import Metric from "../../../component/metrics/";
import { AlertRuleFormUtils } from '../../../helpers/AlertRuleFormValidation';
import AlertRulePreview from './AlertRulePreview';
import IntegrationType from '../../../types/integration/IntegrationType';
import {
    NOTIFICATION_INTEGRATIONS,
    NOTIFICATION_PROFILE_PROPERTY,
    NotificationIntegrationKind,
    NotificationIntegrationProfile,
    parseNotificationProfiles
} from '../../../helpers/notificationIntegrations';

const SEVERITY_META: Record<number, { label: string; color: string }> = {
    1: { label: 'Critical', color: '#dc2626' },
    2: { label: 'Warning',  color: '#f59e0b' },
    3: { label: 'Info',     color: '#2563eb' },
};

const SectionCard = ({ icon, title, hint, children }: {
    icon: string;
    title: string;
    hint?: string;
    children: React.ReactNode;
}) => (
    <div className="card mb-3">
        <div className="card-header">
            <i className={`fal ${icon} fa-fw mr-10`} aria-hidden="true"/>
            {title}
            {hint && <small className="text-muted ms-2">— {hint}</small>}
        </div>
        <div className="card-body">
            <div className="row g-0 form">
                {children}
            </div>
        </div>
    </div>
);

function Detail(props: {
    alertRules: AlertRules[],
    refresh: Function,
    instances: InstanceTarget[],
    hosts: HostTarget[],
    loading: number
}) {
    const {
        formState,
        action,
        updateFormData,
        validateName,
        validateThresholdArray,
        validateEvaluationWindow,
        validateStatistic,
        handleSubmit,
        resetFieldsForType,
        sortedInstances,
        sortedHosts
    } = useAlertRuleForm({
        alertRules: props.alertRules,
        instances: props.instances,
        hosts: props.hosts,
        loading: props.loading,
        refresh: props.refresh
    });

    const { formData, validation, uiState } = formState;
    const isDelete = action.type === 'delete';
    const typeValue = AlertRuleFormUtils.buildTypeString(formData.entitytype, formData.metrictype);

    const updateThresholdRow = (index: number, patch: Partial<{ threshold: number; severity: number }>) => {
        const next = formData.thresholdarray.map((t, i) => i === index ? { ...t, ...patch } : t);
        updateFormData({ thresholdarray: next });
    };

    const [availableTags, setAvailableTags] = useState<{ tagname: string; tagvalue: string }[]>([]);
    const [notificationProfiles, setNotificationProfiles] = useState<Record<NotificationIntegrationKind, NotificationIntegrationProfile[]>>({
        email: [],
        pagerduty: [],
        servicenow: [],
        slack: [],
        webhook: []
    });
    const getNotificationSelection = (kind: NotificationIntegrationKind): string => {
        return formData.properties.find(p => p.name === NOTIFICATION_PROFILE_PROPERTY[kind])?.value || '';
    };

    const getDefaultNotificationLabel = (kind: NotificationIntegrationKind): string => {
        const defaultProfile = notificationProfiles[kind].find(profile => profile.isDefault);

        if (!defaultProfile?.enabled) {
            return 'Default (Not enabled)';
        }

        return `Default (${defaultProfile.label})`;
    };

    const setNotificationSelection = (kind: NotificationIntegrationKind, value: string) => {
        const propertyName = NOTIFICATION_PROFILE_PROPERTY[kind];
        const next = formData.properties.filter(p => p.name !== propertyName);

        if (value) {
            next.push({ name: propertyName, value });
        }

        updateFormData({ properties: next });
    };

    useEffect(() => {
        const controller = new AbortController();
        TagApi.getAllRawTags(controller.signal).then(rows => {
            const seen = new Set<string>();
            const dedup: { tagname: string; tagvalue: string }[] = [];
            (rows ?? []).forEach(r => {
                const key = `${r.tagname}=${r.tagvalue}`;
                if (!seen.has(key) && r.tagname && r.tagvalue) {
                    seen.add(key);
                    dedup.push({ tagname: r.tagname, tagvalue: r.tagvalue });
                }
            });
            dedup.sort((a, b) => `${a.tagname}=${a.tagvalue}`.localeCompare(`${b.tagname}=${b.tagvalue}`));
            setAvailableTags(dedup);
        });
        return () => controller.abort();
    }, []);

    useEffect(() => {
        api.get<IntegrationType[]>('/parameter').then(response => {
            setNotificationProfiles({
                email: parseNotificationProfiles(response.data, NOTIFICATION_INTEGRATIONS.email),
                pagerduty: parseNotificationProfiles(response.data, NOTIFICATION_INTEGRATIONS.pagerduty),
                servicenow: parseNotificationProfiles(response.data, NOTIFICATION_INTEGRATIONS.servicenow),
                slack: parseNotificationProfiles(response.data, NOTIFICATION_INTEGRATIONS.slack),
                webhook: parseNotificationProfiles(response.data, NOTIFICATION_INTEGRATIONS.webhook)
            });
        }).catch(error => {
            console.error('Failed to retrieve notification integrations.', error);
        });
    }, []);

    const tagOptions = availableTags.map(t => ({
        label: `${t.tagname}=${t.tagvalue}`,
        value: `${t.tagname}=${t.tagvalue}`
    }));
    const selectedTagValues = formData.tag.map(t => `${t.name}=${t.value}`);
    const onTagChange = (values: string[]) => {
        const next = values.map(v => {
            const [name, ...rest] = v.split('=');
            return { name, value: rest.join('=') };
        });
        updateFormData({ tag: next });
    };

    const hostOptions = sortedHosts.map(h => ({ label: h.name, value: h.id }));
    const instanceOptions = sortedInstances.map(i => ({ label: i.name, value: i.id }));

    // Derive the set of host/datasource types implied by the current scope selection.
    // null = no scope filter, accept all types (i.e. show all metrics for the entity).
    // Otherwise an array of allowed type strings (oracle, postgres, ...).
    const scopeTypes: string[] | null = React.useMemo(() => {
        if (formData.entitytype === 'host') {
            if (formData.host.length === 0) return null;
            const selected = sortedHosts.filter(h => formData.host.includes(h.id));
            return Array.from(new Set(selected.map(h => h.type)));
        }
        const noFilter = formData.datasource.length === 0 && formData.tag.length === 0;
        if (noFilter) return null;

        const matched = new Set<number>(formData.datasource);
        if (formData.tag.length > 0) {
            sortedInstances.forEach(inst => {
                if (!inst.tags) return;
                const hit = formData.tag.some(t => inst.tags?.[t.name] === t.value);
                if (hit) matched.add(inst.id);
            });
        }
        const types = sortedInstances
            .filter(i => matched.has(i.id))
            .map(i => i.type);
        return Array.from(new Set(types));
    }, [formData.entitytype, formData.host, formData.datasource, formData.tag, sortedHosts, sortedInstances]);

    const scopeIsAll = formData.entitytype === 'host'
        ? formData.host.length === 0
        : formData.datasource.length === 0 && formData.tag.length === 0;

    const scopeLabel = formData.entitytype === 'host' ? 'Hosts' : 'Instances';

    if (props.loading !== DATA_LOADED) return <div className="col"/>;

    if (uiState.isMissing) {
        return (
            <div className="col">
                <div className="card">
                    <div className="card-header">
                        <i className="fal fa-exclamation-triangle fa-fw mr-10" aria-hidden="true"/>
                        {action.title} Alert Rule
                    </div>
                    <div className="card-body">
                        <Alert message="The requested alert rule is either invalid or no longer available."
                               heading="Invalid request" variant="alert-danger"/>
                        <div className="row g-0 form">
                            <div className="col-xs-12 col-md-9 col-lg-6 actions">
                                <Link to="/admin/alert-rules/" role="button" className="btn btn-secondary">
                                    <i className="fal fa-arrow-circle-left fa-fw fa-sm"/><span>Back</span>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
            <div className="col">
                <form onSubmit={handleSubmit}>

                    {(!validation.isFormValid || validation.statistic.hasError) && (
                            <Alert message={generalErrorMessage.text} heading={generalErrorMessage.title}
                                   variant="alert-danger"/>
                    )}

                                        {/* ─── Section: General ─── */}
                                        <SectionCard icon="fa-info-circle" title={`${action.title} Alert Rule`}
                                            hint="Give this rule a recognisable name and choose what it monitors.">

                                            <div className="col-xs-12 col-md-3">
                                                <span className="required">
                                                    <label htmlFor="name" className="reminder">Alert rule name</label>
                                                </span>
                                            </div>
                                            <div className="col-xs-12 col-md-6 col-lg-3">
                                                <input
                                                    type="text"
                                                    id="name"
                                                    maxLength={250}
                                                    value={formData.name}
                                                    onChange={(e) => updateFormData({ name: e.target.value })}
                                                    onBlur={validateName}
                                                    className={`form-control ${validation.name.className}`}
                                                    disabled={uiState.disabled}
                                                    title={validation.name.isUsed ? "Alert rule name already used." : undefined}
                                                    placeholder="e.g. High CPU on production hosts"
                                                    data-lpignore={true}
                                                />
                                                <small className="text-danger">{validation.name.isUsed ? "Alert rule name already used." : ""}</small>
                                            </div>
                                            <div className="w-100"/>

                                            <div className="col-xs-12 col-md-3">
                                                <span className="required">
                                                    <label htmlFor="type" className="reminder">Alert type</label>
                                                </span>
                                            </div>
                                            <div className="col-xs-12 col-md-6 col-lg-3">
                                                <select
                                                    id="type"
                                                    className="form-control"
                                                    onChange={e => resetFieldsForType(e.target.value)}
                                                    value={typeValue}
                                                    disabled={uiState.disabled}
                                                    data-lpignore={true}
                                                >
                                                    <option value='host_status'>Host Status</option>
                                                    <option value='host_statistic'>Host Statistic</option>
                                                    <option value='datasource_status'>Instance Status</option>
                                                    <option value='datasource_statistic'>Instance Statistic</option>
                                                    <option value='datasource_activity'>Instance Activity</option>
                                                </select>
                                            </div>
                                            <div className="w-100"/>

                                            <div className="col-xs-12 col-md-3">
                                                <label className="reminder">Rule status</label>
                                            </div>
                                            <div className="col-xs-12 col-md-6 col-lg-3">
                                                <div className='form-check form-switch'>
                                                    <input
                                                        type="checkbox"
                                                        id="ruleEnabled"
                                                        className="form-check-input"
                                                        disabled={uiState.disabled}
                                                        onChange={() => updateFormData({ disabled: !formData.disabled })}
                                                        checked={!formData.disabled}
                                                        data-lpignore={true}
                                                    />
                                                    <label htmlFor="ruleEnabled" className="form-check-label ml-10">
                                                        {formData.disabled ? 'Disabled' : 'Enabled'}
                                                    </label>
                                                </div>
                                            </div>
                                            <div className="w-100"/>
                                        </SectionCard>

                                        {/* ─── Section: Scope ─── */}
                                        <SectionCard icon="fa-bullseye-arrow" title="Scope"
                                            hint={`Pick specific ${scopeLabel.toLowerCase()} or leave empty to target all.`}>

                                            <div className="col-xs-12 col-md-3">
                                                <label className="reminder">{scopeLabel}</label>
                                            </div>
                                            <div className="col-xs-12 col-md-6 col-lg-3">
                                                {formData.entitytype === 'host' ? (
                                                    <MultiSelect
                                                        value={formData.host}
                                                        options={hostOptions}
                                                        onChange={(e) => updateFormData({ host: e.value })}
                                                        placeholder="All Hosts"
                                                        display="chip"
                                                        filter
                                                        className="w-100 alert-rule-multiselect"
                                                        disabled={uiState.disabled}
                                                    />
                                                ) : (
                                                    <MultiSelect
                                                        value={formData.datasource}
                                                        options={instanceOptions}
                                                        onChange={(e) => updateFormData({ datasource: e.value })}
                                                        placeholder="All Instances"
                                                        display="chip"
                                                        filter
                                                        className="w-100 alert-rule-multiselect"
                                                        disabled={uiState.disabled}
                                                    />
                                                )}
                                                <ConditionalRender if={scopeIsAll}>
                                                    <small className="text-muted">
                                                        <i className="fal fa-globe fa-fw"/> No filter — applies to all {scopeLabel.toLowerCase()}.
                                                    </small>
                                                </ConditionalRender>
                                            </div>
                                            <div className="w-100"/>

                                            <ConditionalRender if={formData.entitytype !== 'host'}>
                                            <div className="col-xs-12 col-md-3">
                                                <label className="reminder">Tags</label>
                                                <small className="text-muted d-block">Match by tag name/value.</small>
                                            </div>
                                            <div className="col-xs-12 col-md-6 col-lg-3">
                                                <MultiSelect
                                                    value={selectedTagValues}
                                                    options={tagOptions}
                                                    onChange={(e) => onTagChange(e.value)}
                                                    placeholder={tagOptions.length === 0 ? 'No tags available' : 'Select tags'}
                                                    display="chip"
                                                    filter
                                                    className="w-100 alert-rule-multiselect"
                                                    disabled={uiState.disabled || tagOptions.length === 0}
                                                />
                                            </div>
                                            <div className="w-100"/>
                                            </ConditionalRender>

                                        </SectionCard>

                                        {/* ─── Section: Condition ─── */}
                                        <ConditionalRender if={formData.metrictype !== 'status'}>
                                        <SectionCard icon="fa-tachometer-alt" title="Condition"
                                            hint="Define the metric and thresholds that trigger this alert.">

                                            <Metric entityType={formData.entitytype}
                                                    metricType={formData.metrictype}
                                                    hostId={formData.host[0] ?? ''}
                                                    datasourceId={formData.datasource[0] ?? ''}
                                                    scopeTypes={scopeTypes}
                                                    disabled={uiState.disabled}
                                                    setAggregation={(value: string) => updateFormData({ aggregation: value })}
                                                    aggregation={formData.aggregation}
                                                    setStatistic={(value: string) => updateFormData({ statistic: value })}
                                                    statisticError={validation.statistic.hasError ? 'invalid' : ''}
                                                    statistic={formData.statistic}
                                                    setUnits={(value: string) => updateFormData({ units: value })}
                                                    isDelete={isDelete}
                                                    hosts={sortedHosts}
                                                    instances={sortedInstances}
                                            />

                                            <div className="col-xs-12 col-md-3">
                                                <label className="reminder">Trigger when value is</label>
                                            </div>
                                            <div className="col-xs-12 col-md-6 col-lg-3">
                                                <div className="d-flex align-items-center">
                                                    <select
                                                        value={formData.decreasing}
                                                        onChange={(e) => updateFormData({ decreasing: e.target.value })}
                                                        className="form-control mr-10"
                                                        style={{ flex: '1 1 0' }}
                                                        disabled={uiState.disabled}
                                                        data-lpignore={true}
                                                    >
                                                        <option value='false'>&ge; greater or equal</option>
                                                        <option value='true'>&le; less or equal</option>
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="w-100"/>

                                            <div className="col-xs-12 col-md-3">
                                                <span className="required">
                                                    <label className="reminder">Severity thresholds</label>
                                                </span>
                                                <small className="text-muted d-block">Up to one per severity level.</small>
                                            </div>
                                            <div className="col-xs-12 col-md-6 col-lg-3">
                                                {formData.thresholdarray.map((t, i) => {
                                                    const meta = SEVERITY_META[t.severity] ?? SEVERITY_META[1];
                                                    const isEmpty = isNaN(t.threshold);
                                                    return (
                                                        <div key={i} className="d-flex mb-2 align-items-center"
                                                             style={{
                                                                 borderLeft: `4px solid ${meta.color}`,
                                                                 paddingLeft: 8,
                                                                 opacity: isEmpty ? 0.55 : 1
                                                             }}>
                                                            <span className="mr-10" style={{ flex: '0 0 90px', fontWeight: 500 }}>
                                                                {meta.label}
                                                            </span>
                                                            <span className="text-muted mr-10" style={{ flex: '0 0 auto' }}>at</span>
                                                            <input
                                                                type="number"
                                                                id={`threshold-${i}`}
                                                                className="form-control mr-10 no-arrows"
                                                                style={{ flex: '1 1 0', minWidth: 120 }}
                                                                placeholder="value"
                                                                value={isEmpty ? '' : t.threshold}
                                                                onChange={(e) => updateThresholdRow(i, {
                                                                    threshold: e.target.value === '' ? NaN : Number(e.target.value)
                                                                })}
                                                                onBlur={validateThresholdArray}
                                                                disabled={uiState.disabled}
                                                                data-lpignore={true}
                                                            />
                                                            <span className="text-muted mr-10" style={{ flex: '0 0 40px' }}>
                                                                {formData.units || ''}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                                <ConditionalRender if={!!validation.thresholdarray.message}>
                                                    <div><small className="text-danger">{validation.thresholdarray.message}</small></div>
                                                </ConditionalRender>
                                            </div>
                                            <div className="w-100"/>

                                        </SectionCard>
                                        </ConditionalRender>

                                        {/* ─── Section: Evaluation ─── */}
                                        <SectionCard icon="fa-clock" title="Evaluation"
                                            hint="How often the rule runs and over what time window.">

                                            <div className="col-xs-12 col-md-3">
                                                <label htmlFor="frequency" className="reminder">Frequency (mins)</label>
                                                <small className="text-muted d-block">How often the rule is checked (1–{FREQUENCY_MAX}).</small>
                                            </div>
                                            <div className="col-xs-12 col-md-6 col-lg-3">
                                                <input
                                                    type="number"
                                                    id="frequency"
                                                    min={1}
                                                    max={FREQUENCY_MAX}
                                                    step={1}
                                                    value={isNaN(formData.intervalminutes) ? '' : formData.intervalminutes}
                                                    onChange={(e) => updateFormData({ intervalminutes: Number(e.target.value) })}
                                                    className="form-control"
                                                    style={{ maxWidth: 120 }}
                                                    disabled={uiState.disabled}
                                                    data-lpignore={true}
                                                />
                                            </div>
                                            <div className="w-100"/>

                                            <div className="col-xs-12 col-md-3">
                                                <span className="required">
                                                    <label htmlFor="evaluation" className="reminder">Window (mins)</label>
                                                </span>
                                                <small className="text-muted d-block">Time range aggregated for each check (max {EVALUATION_WINDOW_MAX}).</small>
                                            </div>
                                            <div className="col-xs-12 col-md-6 col-lg-3">
                                                <input
                                                    type="number"
                                                    id="evaluation"
                                                    min={1}
                                                    max={EVALUATION_WINDOW_MAX}
                                                    step={1}
                                                    value={isNaN(formData.evaluationwindowminutes) ? '' : formData.evaluationwindowminutes}
                                                    onChange={(e) => updateFormData({ evaluationwindowminutes: Number(e.target.value) })}
                                                    onBlur={validateEvaluationWindow}
                                                    className={`form-control ${validation.evaluationwindow.isValid ? '' : 'is-invalid'}`}
                                                    style={{ maxWidth: 120 }}
                                                    disabled={uiState.disabled}
                                                    data-lpignore={true}
                                                />
                                                <ConditionalRender if={!!validation.evaluationwindow.message}>
                                                    <small className="text-danger">{validation.evaluationwindow.message}</small>
                                                </ConditionalRender>
                                            </div>
                                            <div className="w-100"/>

                                        </SectionCard>

                                        <ConditionalRender if={formData.metrictype !== 'status'}>
                                            <div className="row g-0 form">
                                                <AlertRulePreview
                                                    formData={formData}
                                                    instances={sortedInstances}
                                                    hosts={sortedHosts}
                                                />
                                            </div>
                                        </ConditionalRender>

                                        {/* ─── Section: Notifications / Properties ─── */}
                                        <SectionCard icon="fa-bell" title="Notifications"
                                            hint="Choose notification profiles to use when this alert is triggered. Profiles can be configured in the Integrations section of the admin settings.">

                                            {(['email', 'pagerduty', 'servicenow', 'slack', 'webhook'] as NotificationIntegrationKind[]).map(kind => (
                                                <React.Fragment key={kind}>
                                                    <div className="col-xs-12 col-md-3">
                                                        <label className="reminder">{NOTIFICATION_INTEGRATIONS[kind].label} profile</label>
                                                    </div>
                                                    <div className="col-xs-12 col-md-6 col-lg-3">
                                                        <select
                                                            className="form-control"
                                                            value={getNotificationSelection(kind)}
                                                            onChange={(e) => setNotificationSelection(kind, e.target.value)}
                                                            disabled={uiState.disabled}
                                                            data-lpignore={true}
                                                        >
                                                            <option value="">{getDefaultNotificationLabel(kind)}</option>
                                                            {notificationProfiles[kind]
                                                                .filter(profile => !profile.isDefault &&
                                                                    (profile.enabled || profile.prefix === getNotificationSelection(kind)))
                                                                .map(profile => (
                                                                    <option key={profile.prefix} value={profile.prefix}>
                                                                        {profile.label}
                                                                    </option>
                                                                ))}
                                                            <option value="none">None</option>
                                                        </select>
                                                    </div>
                                                    <div className="w-100"/>
                                                </React.Fragment>
                                            ))}

                                            <div className="w-100"/>

                                        </SectionCard>

                                        {/* Form Actions */}
                                        <div className="row g-0 form">
                                            <div className="col-12 actions">
                                                <button onClick={() => window.history.back()} type="button" className="btn btn-secondary">
                                                    <i className="fal fa-times-circle fa-fw fa-sm"/><span>Cancel</span>
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        validateStatistic();
                                                        handleSubmit(e);
                                                    }}
                                                    type='submit'
                                                    disabled={uiState.loading}
                                                    className={`btn btn-${action.variant}`}>
                                                    <i className={`${action.icon} fa-sm`}/><span>{action.title}</span>
                                                </button>
                                            </div>
                                        </div>
                </form>
            </div>
    )
}

export default Detail;
